import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import axios from "axios";
import API_ENDPOINTS from "config/api";

import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

// Componente simple de estadística
function StatCard({ title, value, icon, color }) {
  return (
    <Card>
      <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
        <MDBox>
          <MDTypography variant="button" color="text" fontWeight="regular">
            {title}
          </MDTypography>
          <MDTypography variant="h4" fontWeight="bold">
            {value}
          </MDTypography>
        </MDBox>
        <MDBox
          width="4rem"
          height="4rem"
          borderRadius="md"
          display="flex"
          justifyContent="center"
          alignItems="center"
          color="white"
          bgColor={color}
          variant="gradient"
        >
          <Icon fontSize="large">{icon}</Icon>
        </MDBox>
      </MDBox>
    </Card>
  );
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

export default function Reportes() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [fechaInicio, setFechaInicio] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split("T")[0];
  });
  const [fechaFin, setFechaFin] = useState(() => new Date().toISOString().split("T")[0]);

  const [estadisticas, setEstadisticas] = useState({
    total_ventas: 0,
    total_ingresos: 0,
    promedio_venta: 0,
    total_impuestos: 0,
  });

  const [productosPopulares, setProductosPopulares] = useState([]);
  const [ventasPorDia, setVentasPorDia] = useState([]);
  const [loading, setLoading] = useState(false);

  // Función para formatear moneda en formato compacto con $ al inicio
  const formatCurrencyCompact = (value) => {
    const formatted = new Intl.NumberFormat("es-MX", {
      notation: "compact",
      compactDisplay: "short",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
    return `$${formatted}`;
  };

  useEffect(() => {
    fetchReportes();
  }, []);

  const fetchReportes = async () => {
    try {
      setLoading(true);

      // Obtener reporte general
      const reporteResponse = await axios.get(API_ENDPOINTS.VENTAS_REPORTE, {
        params: {
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
        },
      });

      setEstadisticas({
        total_ventas: reporteResponse.data.total_ventas || 0,
        total_ingresos: reporteResponse.data.total_ingresos || 0,
        promedio_venta: reporteResponse.data.promedio_venta || 0,
        total_impuestos: reporteResponse.data.total_impuestos || 0,
      });

      // Convertir ventas por día a array
      if (reporteResponse.data.ventas_por_dia) {
        const ventasDiaArray = Object.entries(reporteResponse.data.ventas_por_dia).map(
          ([fecha, datos]) => ({
            fecha,
            cantidad: datos.cantidad,
            total: datos.total,
          })
        );
        setVentasPorDia(ventasDiaArray);
      }

      // Obtener productos populares
      const productosResponse = await axios.get(API_ENDPOINTS.VENTAS_PRODUCTOS_POPULARES);
      setProductosPopulares(productosResponse.data || []);

      setLoading(false);
    } catch (error) {
      console.error("Error al cargar reportes:", error);
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();

      // Título
      doc.setFontSize(18);
      doc.setTextColor(40);
      doc.text("Reporte de Ventas", 14, 22);

      // Metadatos
      doc.setFontSize(11);
      doc.setTextColor(100);
      doc.text(`Generado: ${new Date().toLocaleString()}`, 14, 30);
      doc.text(`Período: ${fechaInicio} al ${fechaFin}`, 14, 36);

      // --- Tabla 1: Resumen General ---
      doc.setFontSize(14);
      doc.setTextColor(40);
      doc.text("Resumen General", 14, 45);

      const resumenData = [
        ["Total Ventas", estadisticas.total_ventas.toString()],
        [
          "Total Ingresos",
          new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
            estadisticas.total_ingresos
          ),
        ],
        [
          "Promedio Venta",
          new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
            estadisticas.promedio_venta
          ),
        ],
        [
          "Total Impuestos",
          new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
            estadisticas.total_impuestos
          ),
        ],
      ];

      doc.autoTable({
        startY: 50,
        head: [["Métrica", "Valor"]],
        body: resumenData,
        theme: "striped",
        headStyles: { fillColor: [52, 71, 103] }, // Color del dashboard (aprox)
        styles: { fontSize: 10 },
      });

      let finalY = doc.lastAutoTable.finalY || 50;

      // --- Tabla 2: Top 10 Productos ---
      if (productosPopulares.length > 0) {
        doc.text("Top 10 Productos Más Vendidos", 14, finalY + 10);

        const productosBody = productosPopulares.map((item, index) => [
          (index + 1).toString(),
          item.nombre,
          item.total_vendido.toString(),
          new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
            item.total_ingresos
          ),
        ]);

        doc.autoTable({
          startY: finalY + 15,
          head: [["#", "Producto", "Unidades Vendidas", "Ingresos Totales"]],
          body: productosBody,
          theme: "grid",
          headStyles: { fillColor: [40, 167, 69] }, // Verde success
          styles: { fontSize: 9 },
        });

        finalY = doc.lastAutoTable.finalY;
      }

      // --- Tabla 3: Ventas por Día ---
      // Verificamos si cabe en la página actual, si no, nueva página
      if (finalY > 200) {
        doc.addPage();
        finalY = 20;
      } else {
        finalY += 10;
      }

      doc.text("Detalle Cronológico de Ventas", 14, finalY);

      const ventasBody = ventasPorDia.map((item) => [
        new Date(item.fecha).toLocaleDateString("es-ES"),
        item.cantidad,
        new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(item.total),
      ]);

      doc.autoTable({
        startY: finalY + 5,
        head: [["Fecha", "Cantidad de Ventas", "Total Facturado"]],
        body: ventasBody,
        theme: "grid",
        headStyles: { fillColor: [23, 162, 184] }, // Info color equivalent
      });

      doc.save(`reporte_ventas_${fechaInicio}_al_${fechaFin}.pdf`);
    } catch (error) {
      console.error("Error al exportar PDF:", error);
      alert("Hubo un error al generar el PDF.");
    }
  };

  const handleExportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      // --- Hoja 1: Resumen ---
      const resumenWS = XLSX.utils.json_to_sheet([
        { Concepto: "Reporte de Ventas", Valor: "" },
        { Concepto: "Periodo Inicio", Valor: fechaInicio },
        { Concepto: "Periodo Fin", Valor: fechaFin },
        { Concepto: "", Valor: "" },
        { Concepto: "Total Ventas", Valor: estadisticas.total_ventas },
        { Concepto: "Total Ingresos", Valor: estadisticas.total_ingresos },
        { Concepto: "Promedio Venta", Valor: estadisticas.promedio_venta },
        { Concepto: "Total Impuestos", Valor: estadisticas.total_impuestos },
      ]);
      XLSX.utils.book_append_sheet(wb, resumenWS, "Resumen General");

      // --- Hoja 2: Top Productos ---
      if (productosPopulares.length > 0) {
        const productosData = productosPopulares.map((p) => ({
          Producto: p.nombre,
          "Precio Base": p.precio || 0,
          "Unidades Vendidas": p.total_vendido,
          "Total Ingresos": p.total_ingresos,
        }));
        const productosWS = XLSX.utils.json_to_sheet(productosData);
        XLSX.utils.book_append_sheet(wb, productosWS, "Top Productos");
      }

      // --- Hoja 3: Ventas por Día ---
      if (ventasPorDia.length > 0) {
        const ventasData = ventasPorDia.map((v) => ({
          Fecha: v.fecha,
          "Cantidad Ventas": v.cantidad,
          "Total Facturado": v.total,
        }));
        const ventasWS = XLSX.utils.json_to_sheet(ventasData);
        XLSX.utils.book_append_sheet(wb, ventasWS, "Ventas por Día");
      }

      XLSX.writeFile(wb, `reporte_ventas_${fechaInicio}_al_${fechaFin}.xlsx`);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      alert("Hubo un error al generar el Excel.");
    }
  };

  return (
    <MDBox>
      {/* Filtros de Fecha */}
      <Card sx={{ mb: 3 }}>
        <MDBox p={3}>
          <MDTypography variant="h6" mb={2}>
            Período de Análisis
          </MDTypography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                label="Fecha Inicio"
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Fecha Fin"
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDButton variant="gradient" color={sidenavColor} onClick={fetchReportes} fullWidth>
                <Icon>analytics</Icon>&nbsp;Generar Reporte
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      {/* Estadísticas Principales */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Ventas"
            value={estadisticas.total_ventas.toLocaleString("es-MX")}
            icon="receipt_long"
            color="primary"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Ingresos"
            value={formatCurrencyCompact(parseFloat(estadisticas.total_ingresos || 0))}
            icon="attach_money"
            color="success"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Promedio por Venta"
            value={formatCurrencyCompact(parseFloat(estadisticas.promedio_venta || 0))}
            icon="trending_up"
            color="info"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Impuestos"
            value={formatCurrencyCompact(parseFloat(estadisticas.total_impuestos || 0))}
            icon="account_balance"
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Ventas por Día */}
        <Grid item xs={12} md={7}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" mb={2}>
                <Icon sx={{ mr: 1 }}>show_chart</Icon>
                Ventas por Día
              </MDTypography>
              {loading ? (
                <MDTypography variant="body2" color="text">
                  Cargando datos...
                </MDTypography>
              ) : ventasPorDia.length === 0 ? (
                <MDTypography variant="body2" color="text">
                  No hay datos para el período seleccionado
                </MDTypography>
              ) : (
                <MDBox sx={{ maxHeight: "400px", overflow: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "12px",
                            borderBottom: "2px solid #e0e0e0",
                          }}
                        >
                          <MDTypography variant="button" fontWeight="bold">
                            Fecha
                          </MDTypography>
                        </th>
                        <th
                          style={{
                            textAlign: "center",
                            padding: "12px",
                            borderBottom: "2px solid #e0e0e0",
                          }}
                        >
                          <MDTypography variant="button" fontWeight="bold">
                            Cantidad
                          </MDTypography>
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: "12px",
                            borderBottom: "2px solid #e0e0e0",
                          }}
                        >
                          <MDTypography variant="button" fontWeight="bold">
                            Total
                          </MDTypography>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ventasPorDia.map((dia, index) => (
                        <tr key={index} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "12px" }}>
                            <MDTypography variant="body2">
                              {new Date(dia.fecha).toLocaleDateString("es-ES")}
                            </MDTypography>
                          </td>
                          <td style={{ padding: "12px", textAlign: "center" }}>
                            <MDTypography variant="body2">{dia.cantidad}</MDTypography>
                          </td>
                          <td style={{ padding: "12px", textAlign: "right" }}>
                            <MDTypography variant="body2" color="success" fontWeight="medium">
                              {new Intl.NumberFormat("es-MX", {
                                style: "currency",
                                currency: "MXN",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(parseFloat(dia.total))}
                            </MDTypography>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </Grid>

        {/* Productos Más Vendidos */}
        <Grid item xs={12} md={5}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" mb={2}>
                <Icon sx={{ mr: 1 }}>star</Icon>
                Top 10 Productos Más Vendidos
              </MDTypography>
              {loading ? (
                <MDTypography variant="body2" color="text">
                  Cargando datos...
                </MDTypography>
              ) : productosPopulares.length === 0 ? (
                <MDTypography variant="body2" color="text">
                  No hay datos disponibles
                </MDTypography>
              ) : (
                <MDBox
                  sx={{
                    maxHeight: "400px",
                    overflow: "auto",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
                    "&::-webkit-scrollbar-thumb": { background: "#888", borderRadius: "3px" },
                    "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
                  }}
                >
                  {productosPopulares.map((producto, index) => (
                    <MDBox
                      key={index}
                      p={2}
                      mb={1}
                      sx={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                          borderColor: "#ced4da",
                        },
                      }}
                    >
                      <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDBox>
                          <MDTypography variant="button" fontWeight="medium">
                            {index + 1}. {producto.nombre || "Producto"}
                          </MDTypography>
                          <br />
                          <MDTypography variant="caption" color="text">
                            Vendidos: {producto.total_vendido || 0} unidades
                          </MDTypography>
                        </MDBox>
                        <MDBox textAlign="right">
                          <MDTypography variant="body2" color="success" fontWeight="bold">
                            {new Intl.NumberFormat("es-MX", {
                              style: "currency",
                              currency: "MXN",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(parseFloat(producto.total_ingresos || 0))}
                          </MDTypography>
                          {parseFloat(producto.total_ingresos || 0) === 0 && (
                            <MDTypography variant="caption" color="warning" display="block">
                              Revisar Precio Unitario
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  ))}
                </MDBox>
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* Botones de Exportación */}
      <MDBox display="flex" justifyContent="flex-end" mt={3} gap={2}>
        <MDButton variant="outlined" color={sidenavColor} onClick={handleExportPDF}>
          <Icon>file_download</Icon>&nbsp;Exportar PDF
        </MDButton>
        <MDButton variant="outlined" color="success" onClick={handleExportExcel}>
          <Icon>table_view</Icon>&nbsp;Exportar Excel
        </MDButton>
      </MDBox>
    </MDBox>
  );
}
